import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';

/*
 * Splashtop fleet-wide videographer access. `setWideVGAccess` starts one long-running
 * process (grant/remove); only a single process may run at a time (the server cancels a
 * different in-flight one). `getOperationLocks` reports what is currently in progress so
 * the UI can tell the user about it. A lock existing means a process is running.
 */

export type WideVGAccessOperation = 'grant' | 'remove';

export interface OperationLock {
  id: string;
  process: string;
  service: string;
  created_at?: string;
  updated_at?: string;
}

export interface SetWideVGAccessResponse {
  started: boolean;
  message: string;
  lock?: OperationLock | null;
}

export const WIDE_VG_SERVICE = 'splashtop';

/* TODO: hardcoded group id for testing — replace with the real group id later. */
export const WIDE_VG_GROUP_ID = 250432;

export const wideVGProcess = (operation: WideVGAccessOperation): string =>
  `setWideVGAccess.${operation}`;

const SET_WIDE_VG_ACCESS = gql`
  mutation SetWideVGAccess($groupId: Int!, $operation: WideVGAccessOperation!) {
    setWideVGAccess(groupId: $groupId, operation: $operation) {
      started
      message
      lock {
        id
        process
        service
        created_at
        updated_at
      }
    }
  }
`;

interface SetData {
  setWideVGAccess: SetWideVGAccessResponse;
}

interface SetVars {
  groupId: number;
  operation: WideVGAccessOperation;
}

export const useSetWideVGAccess = (): [
  (operation: WideVGAccessOperation) => Promise<SetWideVGAccessResponse | undefined>,
  { loading: boolean; error?: Error }
] => {
  const [mutation, result] = useMutation<SetData, SetVars>(SET_WIDE_VG_ACCESS);
  const [error, setError] = useState<Error | undefined>();

  const callback = useCallback(
    async (operation: WideVGAccessOperation): Promise<SetWideVGAccessResponse | undefined> => {
      setError(undefined);

      try {
        return await mutation({ variables: { groupId: WIDE_VG_GROUP_ID, operation } })
          .then(({ data }: FetchResult<SetData>) => data?.setWideVGAccess ?? undefined)
          .catch((_error: ApolloError) => {
            throw new Error('Server error');
          });
      } catch (error_: unknown) {
        setError(error_ instanceof Error ? error_ : new Error('Server error'));

        return undefined;
      }
    },
    [mutation]
  );

  return [callback, { loading: result.loading, error }];
};

const GET_OPERATION_LOCKS = gql`
  query GetOperationLocks($service: String!, $process: String) {
    getOperationLocks(service: $service, process: $process) {
      id
      process
      service
      created_at
      updated_at
    }
  }
`;

interface LocksData {
  getOperationLocks: OperationLock[];
}

interface LocksVars {
  process?: string;
  service: string;
}

export const useOperationLocks = (
  service: string,
  process?: string
): { locks: OperationLock[]; loading: boolean; error?: ApolloError; refetch: () => void } => {
  const { data, loading, error, refetch } = useQuery<LocksData, LocksVars>(GET_OPERATION_LOCKS, {
    variables: { service, process },
    fetchPolicy: 'cache-and-network'
  });

  return {
    locks: data?.getOperationLocks ?? [],
    loading,
    error,
    refetch: (): void => {
      void refetch();
    }
  };
};
